import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useSelector } from "react-redux";

import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";

import jsPDF from "jspdf";
import html2canvas from "html2canvas";

import {
    Backdrop,
    Checkbox,
    CircularProgress,
    Drawer,
    Fab,
    Grid,
    IconButton,
    Paper,
    Stack,
    TextField,
    Toolbar,
    Tooltip,
    Typography,
} from "@mui/material";

import RoundedButton from "./RoundedButton";
import GoBack from "./GoBack";

import Icons from "../helpers/icons";
import Colours from "../helpers/colours";

import { AROLOGO, getHotelName } from "../configuration";
import { PDF_BUILDER_TEMPLATES } from "../helpers/PDFTemplates";
import ReportBuilder from "./shared/ReportBuilder/ReportBuilder";
import { selectHotels } from "../features/hotels/hotelsApi";

const OPENED_SIZE = 300;
const CLOSED_SIZE = 20;
const BUTTON_SIZE = 30;

const PDFBuilder = () => {
    const [open, setOpen] = useState(true);

    const templateKey = useSelector((state) => state.PDFExport.templateKey);
    const PDFState = useSelector((state) => state.PDFExport);
    const { id: hotelID } = useSelector((state) => state.hotelID);
    const js = useSelector((state) => state.globalReportFilters);
    const { data: hotels = [] } = useSelector(selectHotels);

    const [data, setData] = useState([]);
    const [reportsOptions, setReportsOptions] = useState({});

    const [fileName, setFileName] = useState("report.pdf");

    const [isDownloading, setIsDownloading] = useState(false);
    const [isReady, setIsReady] = useState(false);
    const [expandedGroups, setExpandedGroups] = useState({});
    const hasActiveReports = data.some(([key]) => reportsOptions[key]?.visible);

    const handleItemsBuilt = useCallback((items) => {
        const auxData = [];
        const auxOptions = {};
        const keyCounts = {};
        items.forEach((item) => {
            const baseName = item.title ?? "item";
            keyCounts[baseName] = (keyCounts[baseName] ?? 0) + 1;
            const key =
                keyCounts[baseName] === 1
                    ? baseName
                    : `${baseName}${keyCounts[baseName]}`;
            auxData.push([key, item.element]);
            auxOptions[key] = { visible: true, ...(item.options ?? {}) };
        });
        setData(auxData);
        setReportsOptions(auxOptions);
        setIsReady(true);
    }, []);

    const templateDescriptors = useMemo(() => {
        return templateKey && PDF_BUILDER_TEMPLATES[templateKey]
            ? PDF_BUILDER_TEMPLATES[templateKey]({ hotelID, js, PDF: true })
            : null;
    }, [templateKey, hotelID, js]);

    useEffect(() => {
        // hotel name with export date DD MM YYYY format - template key
        let aux = `${getHotelName(hotelID, hotels, false)} - ${templateKey ?? "report"} ${new Date().toLocaleDateString()}.pdf`;
        setFileName(aux);
    }, [hotelID, templateKey]);

    const onDragEnd = (result) => {
        if (
            !result.destination ||
            result.source.index === result.destination.index
        )
            return;
        const srcIdx = result.source.index;
        const dstIdx = result.destination.index;
        const [draggedKey] = data[srcIdx];
        const draggedOptions = reportsOptions[draggedKey] ?? {};

        let newData = Array.from(data);

        if (draggedOptions.isGroupHeader) {
            const groupId = draggedOptions.groupId;
            const groupItems = data.filter(
                ([k]) => reportsOptions[k]?.groupId === groupId,
            );
            newData = data.filter(
                ([k]) => reportsOptions[k]?.groupId !== groupId,
            );

            // Map dstIdx (from original data) to the equivalent position in newData
            const insertRaw = data
                .slice(0, dstIdx)
                .filter(([k]) => reportsOptions[k]?.groupId !== groupId).length;
            let insertAt = Math.min(insertRaw, newData.length);

            // If insertAt would split another group, snap to after that group's last child
            if (insertAt > 0 && insertAt < newData.length) {
                const [prevKey] = newData[insertAt - 1];
                const [nextKey] = newData[insertAt];
                const prevOpts = reportsOptions[prevKey] ?? {};
                const nextOpts = reportsOptions[nextKey] ?? {};
                if (
                    prevOpts.groupId &&
                    nextOpts.groupId === prevOpts.groupId &&
                    !nextOpts.isGroupHeader
                ) {
                    const otherGroupId = prevOpts.groupId;
                    let end = insertAt;
                    while (
                        end < newData.length &&
                        reportsOptions[newData[end][0]]?.groupId ===
                            otherGroupId
                    ) {
                        end++;
                    }
                    insertAt = end;
                }
            }

            newData.splice(insertAt, 0, ...groupItems);
        } else if (draggedOptions.groupId) {
            const groupId = draggedOptions.groupId;
            const childIdxs = data
                .map(([k], i) => ({ k, i }))
                .filter(
                    ({ k }) =>
                        reportsOptions[k]?.groupId === groupId &&
                        !reportsOptions[k]?.isGroupHeader,
                )
                .map(({ i }) => i);
            const minDst = Math.min(...childIdxs);
            const maxDst = Math.max(...childIdxs);
            const constrainedDst = Math.max(minDst, Math.min(dstIdx, maxDst));
            const [relocated] = newData.splice(srcIdx, 1);
            newData.splice(constrainedDst, 0, relocated);
        } else {
            const [relocated] = newData.splice(srcIdx, 1);
            newData.splice(dstIdx, 0, relocated);
        }

        setData(newData);
    };

    const handleDelete = (index, key) => {
        const options = reportsOptions[key] ?? {};
        const isGroupHeader = options.isGroupHeader ?? false;
        const groupId = options.groupId;

        if (isGroupHeader) {
            const keysToDelete = new Set(
                data
                    .filter(([k]) => reportsOptions[k]?.groupId === groupId)
                    .map(([k]) => k),
            );
            setData(data.filter(([k]) => !keysToDelete.has(k)));
            setReportsOptions((prev) => {
                const next = { ...prev };
                keysToDelete.forEach((k) => delete next[k]);
                return next;
            });
        } else {
            setData(data.filter((_, i) => i !== index));
            setReportsOptions((prev) => {
                const next = { ...prev };
                delete next[key];
                return next;
            });
        }
    };

    const handleDuplicate = (key, index, report) => {
        const groupId = reportsOptions[key]?.groupId;
        const baseName = key.replace(/ - \d+$/, "");
        const siblings = data
            .filter(([k]) => {
                const sameGroup = groupId
                    ? reportsOptions[k]?.groupId === groupId
                    : !reportsOptions[k]?.groupId;
                return (
                    sameGroup &&
                    (k === baseName || k.startsWith(`${baseName} - `))
                );
            })
            .map(([k]) => {
                const m = k.match(/ - (\d+)$/);
                return m ? parseInt(m[1]) : 1;
            });
        const newNumber = Math.max(1, ...siblings) + 1;
        const copyKey = `${baseName} - ${newNumber}`;
        const origDisplay = reportsOptions[key]?.displayTitle;
        const newDisplay = origDisplay
            ? `${origDisplay.replace(/ - \d+$/, "")} - ${newNumber}`
            : undefined;
        const auxData = [...data];
        auxData.splice(index + 1, 0, [copyKey, report]);
        setData(auxData);
        setReportsOptions((prev) => ({
            ...prev,
            [copyKey]: {
                ...prev[key],
                ...(newDisplay !== undefined
                    ? { displayTitle: newDisplay }
                    : {}),
            },
        }));
    };

    const generatePDF = async () => {
        setIsDownloading(true);
        const element = document.getElementById("content");
        const originalStylesContent = element.style.width;

        element.style.width = "1920px";
        await new Promise((resolve) => setTimeout(resolve, 100));

        const sections = Array.from(element.querySelectorAll(".page-section"));
        const isGroupHeaderArr = sections.map(
            (s) => s.dataset.groupHeader === "true",
        );

        // Aplica los fixes de captura directamente sobre el documento clonado
        // que html2canvas usa para rasterizar, evitando depender de que estilos
        // inyectados en el documento real sobrevivan al clonado.
        const applyPdfCanvasFixes = (clonedDoc) => {
            const style = clonedDoc.createElement("style");
            style.textContent = `
            .MuiOutlinedInput-notchedOutline { top: 0 !important; }
        `;
            clonedDoc.head.appendChild(style);
        };

        try {
            // Pass 1: render all sections to canvases in parallel
            const canvases = await Promise.all(
                sections.map((section) =>
                    html2canvas(section, {
                        scale: 2,
                        onclone: applyPdfCanvasFixes,
                    }),
                ),
            );

            const pdf = new jsPDF("p", "mm", "a4");
            const pageHeight = 297;
            const imgWidth = 210;
            let currentPosition = 0;
            const spaceTop = 7;

            // Pass 2: layout with keep-with-next for group headers
            for (let i = 0; i < sections.length; i++) {
                const canvas = canvases[i];
                const imgHeight = (canvas.height * imgWidth) / canvas.width;
                const isHeader = isGroupHeaderArr[i];

                let willOverflow =
                    currentPosition + spaceTop + imgHeight > pageHeight;

                if (!willOverflow && isHeader && i + 1 < sections.length) {
                    const nextCanvas = canvases[i + 1];
                    const nextImgHeight =
                        (nextCanvas.height * imgWidth) / nextCanvas.width;
                    const afterBoth =
                        currentPosition +
                        spaceTop +
                        imgHeight +
                        spaceTop +
                        nextImgHeight;
                    if (afterBoth > pageHeight && currentPosition > 0) {
                        willOverflow = true;
                    }
                }

                if (willOverflow) {
                    pdf.addPage();
                    currentPosition = 0;
                }

                currentPosition += spaceTop;
                const imgData = canvas.toDataURL("image/jpeg");
                pdf.addImage(
                    imgData,
                    "JPEG",
                    0,
                    currentPosition,
                    imgWidth,
                    imgHeight,
                );
                currentPosition += imgHeight;
            }

            pdf.save(
                fileName.toLowerCase().endsWith(".pdf")
                    ? fileName
                    : fileName + ".pdf",
            );
        } finally {
            element.style.width = originalStylesContent;
            setIsDownloading(false);
        }
    };

    return (
        <>
            <div>
                {templateDescriptors && (
                    <ReportBuilder
                        reports={templateDescriptors}
                        onPDFItemsBuilt={handleItemsBuilt}
                        headless={true}
                        PDF={true}
                    />
                )}
                {!isReady ? (
                    <Stack
                        alignItems="center"
                        justifyContent="center"
                        sx={{ height: "60vh" }}
                    >
                        <CircularProgress />
                    </Stack>
                ) : data.length > 0 ? (
                    <>
                        <div style={{ display: "flex", marginLeft: "1em" }}>
                            <IconButton
                                size="small"
                                style={{
                                    width: BUTTON_SIZE,
                                    height: BUTTON_SIZE,
                                    position: "fixed",
                                    top: "100px",
                                    right: open
                                        ? `${OPENED_SIZE - BUTTON_SIZE / 2}px`
                                        : `${CLOSED_SIZE / 2}px`,
                                    backgroundColor: Colours.backgroundWhite,
                                    zIndex: 1200,
                                    transition:
                                        "all 225ms cubic-bezier(0.4, 0, 0.6, 1) 0ms",
                                    border: `1px solid ${Colours.navbarBottomLine}`,
                                }}
                                onClick={() => setOpen((v) => !v)}
                            >
                                {open ? (
                                    <Icons.ArrowRight fontSize="inherit" />
                                ) : (
                                    <Icons.ArrowLeft fontSize="inherit" />
                                )}
                            </IconButton>

                            <Drawer
                                variant="permanent"
                                open={open}
                                sx={{
                                    position: "absolute",
                                    zIndex: 1199,
                                    width: open ? OPENED_SIZE : CLOSED_SIZE,
                                    transition: (theme) =>
                                        theme.transitions.create("width", {
                                            easing: theme.transitions.easing
                                                .sharp,
                                            duration:
                                                theme.transitions.duration
                                                    .enteringScreen,
                                        }),
                                    "& .MuiDrawer-paper": {
                                        background: "#fafafa",
                                        borderLeft: open
                                            ? `1px solid ${Colours.navbarBottomLine}`
                                            : "transparent",
                                        justifyContent: "space-start",
                                        overflowX: "hidden",
                                        width: open ? OPENED_SIZE : CLOSED_SIZE,
                                        transition: (theme) =>
                                            theme.transitions.create("width", {
                                                easing: theme.transitions.easing
                                                    .sharp,
                                                duration:
                                                    theme.transitions.duration
                                                        .leavingScreen,
                                            }),
                                    },
                                }}
                                anchor={"right"}
                            >
                                {open && (
                                    <>
                                        <Toolbar />
                                        <DragDropContext onDragEnd={onDragEnd}>
                                            <Droppable droppableId="droppable">
                                                {(provided) => (
                                                    <div
                                                        ref={provided.innerRef}
                                                        {...provided.droppableProps}
                                                    >
                                                        <Typography
                                                            variant="subtitle2"
                                                            sx={{
                                                                px: 2,
                                                                py: 1,
                                                                fontWeight:
                                                                    "bold",
                                                                borderBottom: `1px solid ${Colours.navbarBottomLine}`,
                                                            }}
                                                        >
                                                            Selected Reports
                                                        </Typography>
                                                        {data.map(
                                                            (
                                                                [key, report],
                                                                index,
                                                            ) => {
                                                                const itemOpts =
                                                                    reportsOptions[
                                                                        key
                                                                    ] ?? {};
                                                                const isGroupHeader =
                                                                    itemOpts.isGroupHeader ??
                                                                    false;
                                                                const groupId =
                                                                    itemOpts.groupId;
                                                                const isGroupChild =
                                                                    !!(
                                                                        groupId &&
                                                                        !isGroupHeader
                                                                    );
                                                                const isExpanded =
                                                                    expandedGroups[
                                                                        groupId
                                                                    ] ?? false;
                                                                const isVisible =
                                                                    itemOpts.visible ??
                                                                    true;
                                                                const isDuplicable =
                                                                    itemOpts.duplicable ??
                                                                    false;
                                                                return (
                                                                    <Draggable
                                                                        key={
                                                                            key
                                                                        }
                                                                        draggableId={
                                                                            key
                                                                        }
                                                                        index={
                                                                            index
                                                                        }
                                                                    >
                                                                        {(
                                                                            provided,
                                                                        ) => (
                                                                            <div
                                                                                ref={
                                                                                    provided.innerRef
                                                                                }
                                                                                {...provided.draggableProps}
                                                                                style={{
                                                                                    ...(isGroupChild &&
                                                                                    !isExpanded
                                                                                        ? {
                                                                                              height: 0,
                                                                                              overflow:
                                                                                                  "hidden",
                                                                                          }
                                                                                        : {
                                                                                              background:
                                                                                                  isGroupHeader
                                                                                                      ? "#f0f0f0"
                                                                                                      : "#fafafa",
                                                                                              borderBottom:
                                                                                                  "1px solid lightgrey",
                                                                                          }),
                                                                                    ...provided
                                                                                        .draggableProps
                                                                                        .style,
                                                                                }}
                                                                            >
                                                                                <div
                                                                                    style={{
                                                                                        display:
                                                                                            isGroupChild &&
                                                                                            !isExpanded
                                                                                                ? "none"
                                                                                                : "flex",
                                                                                        alignItems:
                                                                                            "center",
                                                                                        padding:
                                                                                            "4px 8px",
                                                                                        paddingLeft:
                                                                                            isGroupChild
                                                                                                ? "24px"
                                                                                                : "8px",
                                                                                    }}
                                                                                    {...provided.dragHandleProps}
                                                                                >
                                                                                    <Tooltip title="Scroll to">
                                                                                        <IconButton
                                                                                            size="small"
                                                                                            sx={{
                                                                                                color: "#bbb",
                                                                                            }}
                                                                                            onClick={() => {
                                                                                                const el =
                                                                                                    document.getElementById(
                                                                                                        `pdfitem-${index}`,
                                                                                                    );
                                                                                                if (
                                                                                                    el
                                                                                                )
                                                                                                    el.scrollIntoView(
                                                                                                        {
                                                                                                            behavior:
                                                                                                                "smooth",
                                                                                                            block: "start",
                                                                                                        },
                                                                                                    );
                                                                                            }}
                                                                                        >
                                                                                            <Icons.Link fontSize="small" />
                                                                                        </IconButton>
                                                                                    </Tooltip>
                                                                                    <Tooltip title="Visible">
                                                                                        <Checkbox
                                                                                            size="small"
                                                                                            checked={
                                                                                                isVisible
                                                                                            }
                                                                                            onChange={() => {
                                                                                                if (
                                                                                                    isGroupHeader
                                                                                                ) {
                                                                                                    const newVis =
                                                                                                        !isVisible;
                                                                                                    setReportsOptions(
                                                                                                        (
                                                                                                            prev,
                                                                                                        ) => {
                                                                                                            const next =
                                                                                                                {
                                                                                                                    ...prev,
                                                                                                                };
                                                                                                            Object.entries(
                                                                                                                next,
                                                                                                            ).forEach(
                                                                                                                ([
                                                                                                                    k,
                                                                                                                    v,
                                                                                                                ]) => {
                                                                                                                    if (
                                                                                                                        v.groupId ===
                                                                                                                        groupId
                                                                                                                    )
                                                                                                                        next[
                                                                                                                            k
                                                                                                                        ] =
                                                                                                                            {
                                                                                                                                ...v,
                                                                                                                                visible:
                                                                                                                                    newVis,
                                                                                                                            };
                                                                                                                },
                                                                                                            );
                                                                                                            return next;
                                                                                                        },
                                                                                                    );
                                                                                                } else {
                                                                                                    setReportsOptions(
                                                                                                        (
                                                                                                            prev,
                                                                                                        ) => ({
                                                                                                            ...prev,
                                                                                                            [key]: {
                                                                                                                ...prev[
                                                                                                                    key
                                                                                                                ],
                                                                                                                visible:
                                                                                                                    !prev[
                                                                                                                        key
                                                                                                                    ]
                                                                                                                        .visible,
                                                                                                            },
                                                                                                        }),
                                                                                                    );
                                                                                                }
                                                                                            }}
                                                                                        />
                                                                                    </Tooltip>
                                                                                    <Typography
                                                                                        variant="body2"
                                                                                        sx={{
                                                                                            flex: 1,
                                                                                            fontSize:
                                                                                                "0.75rem",
                                                                                            fontWeight:
                                                                                                isGroupHeader
                                                                                                    ? "bold"
                                                                                                    : "normal",
                                                                                            overflow:
                                                                                                "hidden",
                                                                                            textOverflow:
                                                                                                "ellipsis",
                                                                                            whiteSpace:
                                                                                                "nowrap",
                                                                                        }}
                                                                                    >
                                                                                        {reportsOptions[
                                                                                            key
                                                                                        ]
                                                                                            ?.displayTitle ??
                                                                                            key}
                                                                                    </Typography>
                                                                                    {isGroupHeader && (
                                                                                        <IconButton
                                                                                            size="small"
                                                                                            onClick={(
                                                                                                e,
                                                                                            ) => {
                                                                                                e.stopPropagation();
                                                                                                setExpandedGroups(
                                                                                                    (
                                                                                                        prev,
                                                                                                    ) => ({
                                                                                                        ...prev,
                                                                                                        [groupId]:
                                                                                                            !isExpanded,
                                                                                                    }),
                                                                                                );
                                                                                            }}
                                                                                        >
                                                                                            {isExpanded ? (
                                                                                                <Icons.ArrowUp fontSize="small" />
                                                                                            ) : (
                                                                                                <Icons.ArrowDown fontSize="small" />
                                                                                            )}
                                                                                        </IconButton>
                                                                                    )}
                                                                                    {isDuplicable && (
                                                                                        <Tooltip title="Duplicate">
                                                                                            <IconButton
                                                                                                size="small"
                                                                                                onClick={() =>
                                                                                                    handleDuplicate(
                                                                                                        key,
                                                                                                        index,
                                                                                                        report,
                                                                                                    )
                                                                                                }
                                                                                            >
                                                                                                <Icons.Add fontSize="small" />
                                                                                            </IconButton>
                                                                                        </Tooltip>
                                                                                    )}
                                                                                    <Tooltip title="Delete">
                                                                                        <IconButton
                                                                                            size="small"
                                                                                            onClick={() =>
                                                                                                handleDelete(
                                                                                                    index,
                                                                                                    key,
                                                                                                )
                                                                                            }
                                                                                        >
                                                                                            <Icons.Delete fontSize="small" />
                                                                                        </IconButton>
                                                                                    </Tooltip>
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                    </Draggable>
                                                                );
                                                            },
                                                        )}
                                                        {provided.placeholder}
                                                    </div>
                                                )}
                                            </Droppable>
                                        </DragDropContext>
                                        <br />
                                        <Stack
                                            direction={"column"}
                                            sx={{ padding: 2 }}
                                        >
                                            {/* <label>
                                    Print Scale:
                                    <input
                                        type="number"
                                        value={printScale}
                                        onChange={(e) =>
                                            setPrintScale(
                                                e.target.value
                                            )
                                        }
                                        min="0.1"
                                        step="0.1"
                                    />
                                </label> */}
                                            <TextField
                                                label="Document Title"
                                                variant="filled"
                                                multiline
                                                value={fileName}
                                                onChange={(e) =>
                                                    setFileName(e.target.value)
                                                }
                                                required
                                                sx={{ width: "100%" }}
                                            />

                                            <RoundedButton
                                                id="pdf-download-button"
                                                onClick={generatePDF}
                                                disabled={
                                                    !hasActiveReports ||
                                                    isDownloading
                                                }
                                                sx={{ mt: 2 }}
                                            >
                                                Download PDF
                                            </RoundedButton>
                                        </Stack>
                                    </>
                                )}
                            </Drawer>
                        </div>

                        <Paper sx={{ padding: 2 }}>
                            <div id="content">
                                {data.map(([key, report], dataIdx) => {
                                    if (
                                        !(reportsOptions[key]?.visible ?? false)
                                    )
                                        return null;
                                    const disableInteraction =
                                        reportsOptions[key]
                                            ?.disableInteraction ?? false;
                                    return (
                                        <div
                                            key={key}
                                            id={`pdfitem-${dataIdx}`}
                                            className="page-section"
                                            data-group-header={
                                                reportsOptions[key]
                                                    ?.isGroupHeader || undefined
                                            }
                                            style={{
                                                scrollMarginTop: "120px",
                                                ...(disableInteraction
                                                    ? { pointerEvents: "none" }
                                                    : {}),
                                            }}
                                        >
                                            <div
                                                style={{
                                                    marginTop: 40,
                                                    marginLeft: 40,
                                                    marginRight: 40,
                                                }}
                                            >
                                                {report}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </Paper>
                    </>
                ) : (
                    <Typography>
                        We couldn't find any report components to generate a
                        PDF. Please try going back and generating them again.
                    </Typography>
                )}
            </div>
            <Backdrop
                open={isDownloading}
                sx={{ zIndex: (theme) => theme.zIndex.drawer + 200 }}
            >
                <Stack alignItems="center" spacing={2}>
                    <CircularProgress sx={{ color: "#fff" }} size={56} />
                    <Typography
                        variant="h6"
                        sx={{ color: "#fff", fontWeight: "bold" }}
                    >
                        Generating PDF…
                    </Typography>
                </Stack>
            </Backdrop>
            {/* Scroll FABs — respect drawer open/close */}
            <Stack
                spacing={1}
                sx={{
                    position: "fixed",
                    bottom: 24,
                    right: open ? OPENED_SIZE + 16 : CLOSED_SIZE + 16,
                    zIndex: 1300,
                    transition: (theme) =>
                        theme.transitions.create("right", {
                            easing: theme.transitions.easing.sharp,
                            duration: theme.transitions.duration.leavingScreen,
                        }),
                }}
            >
                <Tooltip title="Scroll to top" placement="left">
                    <Fab
                        size="small"
                        onClick={() =>
                            window.scrollTo({ top: 0, behavior: "smooth" })
                        }
                    >
                        <Icons.ArrowUp />
                    </Fab>
                </Tooltip>
                <Tooltip title="Scroll to bottom" placement="left">
                    <Fab
                        size="small"
                        onClick={() =>
                            window.scrollTo({
                                top: document.body.scrollHeight,
                                behavior: "smooth",
                            })
                        }
                    >
                        <Icons.ArrowDown />
                    </Fab>
                </Tooltip>
            </Stack>
        </>
    );
};

export default PDFBuilder;
